import {
  inject,
  Injectable,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationExtras,
  Router,
} from '@angular/router';
import {Store} from '@ngrx/store';
import {
  combineLatest,
  filter,
  firstValueFrom,
  Observable,
} from 'rxjs';
import {take} from 'rxjs/operators';
import {AppState} from '../../store/store';
import * as SharedLinkActions from '../../store/shared-link/shared-link.actions';
import {
  selectIsPreviewMode,
  selectSharedLinkData,
  selectSharedLinkId,
} from '../../store/shared-link/shared-link.selectors';
import {updatePortaInfoAndContactInfoByPreviewData} from '../../store/contact/contact.actions';

@Injectable({
  providedIn: 'root',
})
export class PreviewModeService {

  private router: Router = inject(Router);

  private store: Store<AppState> = inject(Store<AppState>);

  readonly previewMode$ = this.store.select(selectIsPreviewMode);

  readonly sharedLinkData$ = this.store.select(selectSharedLinkData);

  /**
   * Initializes shared link handling
   */
  public initialize(): void {
    this.store.dispatch(SharedLinkActions.clearSharedLinkData());
  }

  /**
   * Navigates to the preview page, optionally to a specific subpage
   * @param page
   */
  public async toPreview(page?: string): Promise<boolean> {
    return this.navigateTo(page ? ['preview', page] : ['preview']);
  }

  /**
   * Sets up subscription for URL changes to manage preview mode
   * @returns Observable for URL change handling
   */
  public handleUrlChanges(): Observable<NavigationEnd> {
    return this.router.events.pipe(filter(event => event instanceof NavigationEnd));
  }

  /**
   * Processes URL changes to set or unset preview mode
   * @param url The current navigation URL
   */
  public processNavigationChange(url: string): void {
    if (url.includes('/preview')) {
      this.enablePreviewMode();
    } else {
      this.disablePreviewMode(url);
    }
  }

  /**
   * Processes shared link data to update application state
   * @param data The shared link data
   */
  public processSharedLinkData(data: any): void {
    const payload = data.parsedPayload || data.originalResponse;
    this.store.dispatch(updatePortaInfoAndContactInfoByPreviewData({data: payload}));
  }

  /**
   * Checks if the current URL is in preview mode
   * @returns Whether the current URL includes '/preview'
   */
  public isInPreviewMode(): boolean {
    return this.router.url.includes('/preview');
  }

  /**
   * Enables preview mode and loads shared link data if ID is present
   */
  private enablePreviewMode(): void {
    this.store.dispatch(SharedLinkActions.setPreviewModeActive());

    const urlParams = new URLSearchParams(window.location.search);
    const sharedLinkId = urlParams.get('sharedLinkId');

    if (sharedLinkId) {
      this.store.dispatch(SharedLinkActions.setSharedLinkId({id: sharedLinkId}));
      this.store.dispatch(SharedLinkActions.loadSharedLinkData({id: sharedLinkId}));
    }
  }

  /**
   * Disables preview mode and clears data if on login or home page
   * @param url The current URL
   */
  private disablePreviewMode(url: string): void {
    this.store.dispatch(SharedLinkActions.setPreviewModeInactive());
    if (url.includes('/login') || url === '/') {
      this.store.dispatch(SharedLinkActions.clearSharedLinkData());
    }
  }

  /**
   * Navigate with consideration of preview mode and shared link ID. If in preview mode, appends the sharedLinkId
   * to the query parameters.
   * @param commands - An array of URL fragments to navigate to.
   * @param extras - Optional navigation extras.
   * @returns A promise that resolves to true if navigation was successful, false otherwise.
   */
  private async navigateTo(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    const [isPreviewMode, sharedLinkId] = await firstValueFrom(combineLatest([
      this.store.select(selectIsPreviewMode),
      this.store.select(selectSharedLinkId),
    ]).pipe(take(1)));

    if (isPreviewMode && sharedLinkId) {
      const updatedExtras: NavigationExtras = {
        ...extras,
        queryParams: {...(extras?.queryParams || {}), sharedLinkId},
        queryParamsHandling: 'merge',
      };

      return this.router.navigate(commands, updatedExtras);
    }
    return this.router.navigate(commands, extras);
  }

}
